// ─── Signing progress photos for display ───
// The React half of utils/photoUrls.js: one in-memory cache of signed URLs shared by every
// gallery, filled by a single batched `signed_urls` call for whatever is on screen.
//
// The cache is memory only — it is never dispatched into state, written to localStorage or
// put in the sync payload, because a signed URL is dead within the hour and carries the
// bucket's object key. Photos keep their id as the durable handle.
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiPost } from './api';
import { notifyFeatureDisabled } from './sync';
import { idsToSign, markFailed, markInflight, mergeSigned, photoSrc, putSigned } from './photoUrls';

let CACHE = {};
// Test/sign-out seam: drop every signed URL held for this session.
export const clearPhotoUrlCache = () => { CACHE = {}; };
// The upload response already contains a signed URL — keep it so the new photo renders
// immediately instead of waiting for a second round trip.
export const cacheSignedUrl = (id, url, expiresIn) => { CACHE = putSigned(CACHE, id, url, expiresIn, Date.now()); };

// Returns srcOf(photo) → { src, state }. `photos` may be a fresh array each render: when
// nothing needs signing the effect does a cheap scan and returns.
export function usePhotoUrls(photos, email) {
  const [, bump] = useState(0);
  const busy = useRef(false);
  useEffect(() => {
    if (!email || busy.current) return;
    const ids = idsToSign(photos, CACHE, Date.now());
    if (!ids.length) return;
    let dead = false;
    busy.current = true;
    CACHE = markInflight(CACHE, ids, Date.now());
    (async () => {
      try {
        const r = await apiPost('/api/photos/upload', { action: 'signed_urls', photoIds: ids, email }, { email });
        const urls = (r && r.urls) || {};
        CACHE = mergeSigned(CACHE, urls, r && r.expires_in, Date.now());
        // Ids the server did not sign have no stored object (or belong to someone else):
        // back them off so they are not re-requested on every render.
        CACHE = markFailed(CACHE, ids.filter(id => !urls[id]), Date.now());
      } catch (e) {
        CACHE = markFailed(CACHE, ids, Date.now());
        if (e?.body?.error === 'feature_disabled') notifyFeatureDisabled(e.body.flag || 'photos');
      } finally {
        busy.current = false;
        if (!dead) bump(n => n + 1);
      }
    })();
    return () => { dead = true; };
  }, [photos, email]);
  // Read the cache at call time so a render after a bump sees the new URLs.
  return useCallback((p) => photoSrc(p, CACHE, Date.now()), []);
}
